import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { http, fetchAdminBrand } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Sparkles, Save, Send, Check, Eye, RefreshCw } from "lucide-react";
import BrandCrawlerSettings from "./BrandCrawlerSettings";

const CATS = ["PRODUCT_IDENTITY", "MATERIALS", "ORIGIN", "REPAIR_CARE", "END_OF_LIFE", "FOOTPRINT", "CERTIFICATIONS"];
const LABELS = {
  PRODUCT_IDENTITY: "Product identity",
  MATERIALS: "Materials",
  ORIGIN: "Where it's made",
  REPAIR_CARE: "Repair & care",
  END_OF_LIFE: "End of life",
  FOOTPRINT: "Footprint",
  CERTIFICATIONS: "Certifications",
};

export default function AdminBrandEdit() {
  const { slug } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [brand, setBrand] = useState(null);
  const [profile, setProfile] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [summary, setSummary] = useState({ en: "", it: "" });
  const [dppNote, setDppNote] = useState({ en: "", it: "" });
  const [aiUntouched, setAiUntouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [recentRuns, setRecentRuns] = useState([]);
  const [brandCost, setBrandCost] = useState(null);
  const [progress, setProgress] = useState(null); // { runId, state, kind }
  // Crawler tuning — persisted via PUT /admin/brands/:id/crawler-settings.
  const [crawlerSettings, setCrawlerSettings] = useState({ extra_seed_urls: "", min_coverage_snapshots_override: "" });
  const [crawlerBusy, setCrawlerBusy] = useState(false);

  const loadRecentRuns = (brandId) => {
    if (!brandId) return;
    http.get(`/admin/pipeline/brand-runs/${brandId}?limit=5`)
      .then((r) => setRecentRuns(r.data.runs || []))
      .catch(() => setRecentRuns([]));
    http.get(`/admin/pipeline/brand-cost/${brandId}`)
      .then((r) => setBrandCost(r.data))
      .catch(() => setBrandCost(null));
  };

  const load = () => fetchAdminBrand(slug).then((d) => {
    setBrand(d.brand);
    setProfile(d.profile);
    // Hydrate the crawler-tuning inputs from the loaded brand doc.
    setCrawlerSettings({
      extra_seed_urls: (d.brand?.extra_seed_urls || []).join("\n"),
      min_coverage_snapshots_override: d.brand?.min_coverage_snapshots_override ?? "",
    });
    const cl = CATS.map((c) => {
      const it = d.profile?.checklist?.find((x) => x.category === c) || { category: c, status: "NOT_FOUND", evidence_excerpt: { en: "", it: "" }, source_url: "", notes_internal: "" };
      return {
        category: c,
        status: it.status || "NOT_FOUND",
        evidence_excerpt_en: it.evidence_excerpt?.en || "",
        evidence_excerpt_it: it.evidence_excerpt?.it || "",
        source_url: it.source_url || "",
        notes_internal: it.notes_internal || "",
      };
    });
    setChecklist(cl);
    setSummary({ en: d.profile?.summary?.en || "", it: d.profile?.summary?.it || "" });
    setDppNote({ en: d.profile?.dpp_readiness_note?.en || "", it: d.profile?.dpp_readiness_note?.it || "" });
    setAiUntouched(!!d.profile?.ai_draft_untouched);
    loadRecentRuns(d.brand?.id);
  });
  useEffect(() => { load(); }, [slug]); // eslint-disable-line

  // Poll progress of a single in-flight run (from Force crawl) every 4s
  // until it reaches a terminal state, then refresh the recent-runs
  // panel and the profile.
  useEffect(() => {
    if (!progress?.runId) return undefined;
    const TERMINAL = new Set([
      "SUCCEEDED", "FAILED", "NEEDS_ATTENTION",
      "AWAITING_APPROVAL", "HELD", "PUBLISHED_ACCESS_RESTRICTED",
    ]);
    if (TERMINAL.has(progress.state)) return undefined;
    const timer = setInterval(async () => {
      try {
        const { data } = await http.get(
          `/admin/pipeline/runs-status?ids=${progress.runId}`,
        );
        const row = (data.runs || [])[0];
        if (row) {
          setProgress((p) => (p ? { ...p, state: row.state, outcome: row.outcome } : p));
          if (TERMINAL.has(row.state)) {
            if (brand?.id) loadRecentRuns(brand.id);
            load();
          }
        }
      } catch { /* transient — keep polling */ }
    }, 4000);
    return () => clearInterval(timer);
  }, [progress?.runId, progress?.state]); // eslint-disable-line

  const foundCount = useMemo(() => checklist.filter((c) => c.status === "FOUND" || c.status === "PARTIAL").length, [checklist]);

  const updateItem = (i, patch) => {
    setChecklist((prev) => prev.map((c, idx) => idx === i ? { ...c, ...patch } : c));
  };

  const enforcedStatus = (row) => {
    const hasEvidence = !!(row.evidence_excerpt_en?.trim() || row.evidence_excerpt_it?.trim() || row.source_url?.trim());
    if (!hasEvidence) return "NOT_FOUND";
    if (row.status === "NOT_FOUND") return "PARTIAL";
    return row.status;
  };

  const save = async () => {
    setBusy(true);
    try {
      await http.put(`/admin/brands/${brand.id}/profile`, {
        checklist: checklist.map((c) => ({
          category: c.category,
          status: enforcedStatus(c),
          evidence_excerpt_en: c.evidence_excerpt_en,
          evidence_excerpt_it: c.evidence_excerpt_it,
          source_url: c.source_url || null,
          notes_internal: c.notes_internal,
        })),
        summary_en: summary.en, summary_it: summary.it,
        dpp_note_en: dppNote.en, dpp_note_it: dppNote.it,
        ai_draft_untouched: aiUntouched,
      });
      toast.success("Saved");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const aiDraft = async () => {
    setBusy(true);
    try {
      // Save first (so backend uses latest checklist), then generate
      await save();
      const { data } = await http.post(`/admin/brands/${brand.id}/ai-draft-summary`);
      setSummary(data.summary);
      setAiUntouched(true);
      toast.info("AI draft generated. Please review and edit before submitting.");
    } catch (e) {
      toast.error("AI draft failed");
    } finally { setBusy(false); }
  };

  const submitReview = async () => {
    if (aiUntouched) { toast.error("Please edit the AI draft before submitting for review."); return; }
    setBusy(true);
    try {
      await http.post(`/admin/brands/${brand.id}/submit-for-review`);
      toast.success("Submitted for review");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const approve = async () => {
    setBusy(true);
    try {
      const { data } = await http.post(`/admin/brands/${brand.id}/approve`);
      toast.success(`Approved & published (v${data.version_no})`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approve failed");
    } finally { setBusy(false); }
  };

  const deleteBrand = async () => {
    // Two confirmations because the operation is irreversible.
    if (!window.confirm(
      `Permanently delete "${brand.display_name}" from the platform? ` +
      `This removes the brand, its profile, every profile version, ` +
      `every pipeline run and every changelog entry. Cannot be undone.`,
    )) return;
    const typed = window.prompt(
      `Type the slug "${brand.slug}" to confirm deletion:`,
    );
    if (typed !== brand.slug) {
      toast.error("Slug did not match — deletion cancelled.");
      return;
    }
    setBusy(true);
    try {
      await http.delete(`/admin/brands/${brand.id}`);
      toast.success(`Deleted "${brand.display_name}"`);
      nav("/admin/brands");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
      setBusy(false);
    }
  };

  const saveCrawlerSettings = async () => {
    if (!brand?.id) return;
    setCrawlerBusy(true);
    try {
      const seeds = crawlerSettings.extra_seed_urls
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean);
      const rawOverride = String(crawlerSettings.min_coverage_snapshots_override || "").trim();
      const override = rawOverride === "" ? null : parseInt(rawOverride, 10);
      if (rawOverride !== "" && (!Number.isFinite(override) || override < 1)) {
        toast.error("Minimum pages override must be an integer ≥ 1 (or blank).");
        return;
      }
      await http.put(`/admin/brands/${brand.id}/crawler-settings`, {
        extra_seed_urls: seeds,
        min_coverage_snapshots_override: override,
      });
      toast.success("Crawler settings saved.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save crawler settings");
    } finally { setCrawlerBusy(false); }
  };

  const regenerateDpp = () => {
    if (!brand) return;
    if (brand.sector_code === "FASHION") {
      setDppNote({
        en: `Fashion sector DPP delegated act expected effective 2027 under ESPR. Publishes ${foundCount} of 7 categories.`,
        it: `Atto delegato DPP per il settore moda atteso in vigore nel 2027 ai sensi dell'ESPR. Pubblica ${foundCount} categorie su 7.`,
      });
    } else {
      setDppNote({
        en: `Fashion sector DPP delegated act expected effective 2027 under ESPR. Publishes ${foundCount} of 7 categories.`,
        it: `Atto delegato DPP per il settore moda atteso in vigore nel 2027 ai sensi dell'ESPR. Pubblica ${foundCount} categorie su 7.`,
      });
    }
  };

  // Force a fresh pipeline run for THIS brand. Backend guards against
  // double-enqueue — if a run is already QUEUED/PENDING/RUNNING/
  // AWAITING_APPROVAL we surface it as an info toast instead of erroring.
  const forceCrawl = async () => {
    setBusy(true);
    try {
      const { data } = await http.post(`/admin/pipeline/reprocess/${brand.id}`);
      toast.success(`Crawl re-queued (run ${data.run_id.slice(0, 8)}…)`);
      setProgress({ runId: data.run_id, state: "QUEUED", kind: "REPROCESS" });
      if (brand?.id) loadRecentRuns(brand.id);
    } catch (e) {
      if (e.response?.status === 409) {
        const d = e.response.data?.detail;
        const state = typeof d === "object" ? d.state : null;
        const existingId = typeof d === "object" ? d.run_id : null;
        toast.message(
          `Run already in flight${state ? ` — state: ${state}` : ""}. Tracking the existing run.`,
        );
        if (existingId) {
          setProgress({ runId: existingId, state: state || "RUNNING", kind: (typeof d === "object" ? d.kind : null) || "MANUAL" });
        }
      } else {
        toast.error(e.response?.data?.detail || "Force crawl failed");
      }
    } finally { setBusy(false); }
  };

  if (!brand) return <AdminLayout><div className="bp-container py-24" /></AdminLayout>;

  const reviewState = profile?.review_state || "DRAFT";
  const isAdmin = user?.role === "ADMIN";
  const isGeneralAdmin = Boolean(user?.is_general_admin);
  const isSelfSubmitted = profile?.submitted_by && profile.submitted_by === user?.id;
  // Standard 4-eyes case: profile pending review, current user is NOT
  // the submitter. Admin override: an admin may also publish a DRAFT
  // or NEEDS_ATTENTION profile — this is the escape hatch for brands
  // whose pipeline outcome was partial but that we still want to
  // publish. The 4-eyes rule normally applies even to admin, EXCEPT
  // for the general admin (whitelisted via GENERAL_ADMIN_EMAILS) —
  // requested 2026-08-15, the site owner has unrestricted rights.
  const canApprove =
    (!isSelfSubmitted || isGeneralAdmin) &&
    (
      (reviewState === "PENDING_REVIEW" && profile?.submitted_by)
      || (isAdmin && (reviewState === "NEEDS_ATTENTION" || reviewState === "DRAFT"))
    );
  const approveLabel = isAdmin && reviewState !== "PENDING_REVIEW"
    ? "Force-publish (admin)"
    : "Approve & publish";

  return (
    <AdminLayout>
      <div className="bp-container py-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <Link to="/admin/brands" className="text-xs uppercase tracking-widest bp-link" data-testid="back-to-brands">← Brands</Link>
            <h1 className="font-serif text-4xl mt-2" data-testid="edit-brand-title">{brand.display_name}</h1>
            <div className="text-xs font-mono mt-1" style={{ color: "var(--bp-text-muted)" }}>
              {brand.sector_code} · {brand.country} · slug: {brand.slug} · review: <span className="px-1 border">{reviewState}</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {isAdmin && (
              <button
                onClick={forceCrawl}
                disabled={busy}
                className="text-sm bp-link flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="btn-force-crawl"
                title="Enqueue a fresh pipeline run for this brand"
              >
                <RefreshCw size={14} /> Force crawl now
              </button>
            )}
            <Link to={`/brands/${brand.slug}`} className="text-sm bp-link flex items-center gap-1" data-testid="view-public"><Eye size={14} /> View public →</Link>
          </div>
        </div>

        {/* Checklist rows */}
        <section className="mt-8">
          <h2 className="uppercase text-xs tracking-[0.3em] mb-3" style={{ color: "var(--bp-text-muted)" }}>Transparency checklist</h2>
          <div className="bg-white border" style={{ borderColor: "var(--bp-border)" }}>
            {checklist.map((row, i) => {
              const enforced = enforcedStatus(row);
              const disabled = !(row.evidence_excerpt_en?.trim() || row.evidence_excerpt_it?.trim() || row.source_url?.trim());
              return (
                <div key={row.category} className="p-5 border-b last:border-b-0" style={{ borderColor: "var(--bp-border)" }} data-testid={`edit-row-${row.category}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-serif text-lg">{LABELS[row.category]}</div>
                    <div className="flex gap-1">
                      {["FOUND", "PARTIAL", "NOT_FOUND"].map((s) => (
                        <button
                          key={s}
                          disabled={disabled && s !== "NOT_FOUND"}
                          onClick={() => updateItem(i, { status: s })}
                          className={`px-3 py-1 text-xs uppercase tracking-widest border ${enforced === s ? "bg-black text-white border-black" : ""} ${disabled && s !== "NOT_FOUND" ? "opacity-40 cursor-not-allowed" : ""}`}
                          style={{ borderColor: "var(--bp-border)" }}
                          data-testid={`edit-status-${row.category}-${s}`}
                        >
                          {s.replace("_", " ")}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs uppercase tracking-widest">Evidence excerpt (EN, max 300)</label>
                      <Textarea maxLength={300} value={row.evidence_excerpt_en} onChange={(e) => updateItem(i, { evidence_excerpt_en: e.target.value })} data-testid={`edit-ev-en-${row.category}`} />
                      <div className="text-[10px] font-mono text-right" style={{ color: "var(--bp-text-muted)" }}>{row.evidence_excerpt_en.length}/300</div>
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest">Evidence excerpt (IT, max 300)</label>
                      <Textarea maxLength={300} value={row.evidence_excerpt_it} onChange={(e) => updateItem(i, { evidence_excerpt_it: e.target.value })} data-testid={`edit-ev-it-${row.category}`} />
                      <div className="text-[10px] font-mono text-right" style={{ color: "var(--bp-text-muted)" }}>{row.evidence_excerpt_it.length}/300</div>
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs uppercase tracking-widest">Source URL</label>
                      <Input value={row.source_url} onChange={(e) => updateItem(i, { source_url: e.target.value })} data-testid={`edit-src-${row.category}`} placeholder="https://…" />
                    </div>
                  </div>
                  {disabled && (
                    <p className="mt-2 text-xs italic" style={{ color: "var(--bp-text-muted)" }}>
                      No evidence provided — status is enforced as NOT_FOUND.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Summary */}
        <section className="mt-10">
          <div className="flex items-center justify-between mb-3">
            <h2 className="uppercase text-xs tracking-[0.3em]" style={{ color: "var(--bp-text-muted)" }}>Summary</h2>
            <Button onClick={aiDraft} variant="outline" className="rounded-none" disabled={busy} data-testid="btn-ai-draft"><Sparkles size={14} className="mr-1" /> AI draft</Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest">EN</label>
              <Textarea rows={7} value={summary.en} onChange={(e) => { setSummary({ ...summary, en: e.target.value }); setAiUntouched(false); }} data-testid="edit-summary-en" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest">IT</label>
              <Textarea rows={7} value={summary.it} onChange={(e) => { setSummary({ ...summary, it: e.target.value }); setAiUntouched(false); }} data-testid="edit-summary-it" />
            </div>
          </div>
          {aiUntouched && (
            <p className="mt-2 text-xs" style={{ color: "#B97700" }} data-testid="ai-untouched-warning">
              ⚠ This is an unedited AI draft. Please review and edit before submitting for review.
            </p>
          )}
        </section>

        {/* DPP note */}
        <section className="mt-10">
          <div className="flex items-center justify-between mb-3">
            <h2 className="uppercase text-xs tracking-[0.3em]" style={{ color: "var(--bp-text-muted)" }}>DPP readiness note (auto from sector + FOUND count = {foundCount})</h2>
            <Button variant="outline" className="rounded-none" onClick={regenerateDpp} data-testid="btn-regen-dpp">Regenerate</Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="text-xs uppercase tracking-widest">EN</label><Textarea rows={3} value={dppNote.en} onChange={(e) => setDppNote({ ...dppNote, en: e.target.value })} data-testid="edit-dpp-en" /></div>
            <div><label className="text-xs uppercase tracking-widest">IT</label><Textarea rows={3} value={dppNote.it} onChange={(e) => setDppNote({ ...dppNote, it: e.target.value })} data-testid="edit-dpp-it" /></div>
          </div>
        </section>

        {/* Crawler tuning — per-brand overrides for coverage_gate
            failures. Component extracted to BrandCrawlerSettings.jsx
            during the 2026-08-17 refactor. */}
        <BrandCrawlerSettings
          brand={brand}
          value={crawlerSettings}
          onChange={setCrawlerSettings}
          onSave={saveCrawlerSettings}
          busy={crawlerBusy}
        />

        {/* Recent crawls — last 5 pipeline runs for THIS brand.
            Gives operators an at-a-glance health view of the pipeline
            per brand (kind, state, outcome, duration). */}
        <section className="mt-10" data-testid="recent-crawls-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="uppercase text-xs tracking-[0.3em]" style={{ color: "var(--bp-text-muted)" }}>
              Recent crawls
            </h2>
            <button
              onClick={() => brand?.id && loadRecentRuns(brand.id)}
              className="text-xs bp-link inline-flex items-center gap-1"
              data-testid="recent-crawls-refresh"
              title="Reload recent runs"
            >
              <RefreshCw size={12} /> Refresh
            </button>
          </div>

          {/* Cumulative cost tile — sum across EVERY run ever recorded
              for this brand, not just the 5 in the table below. */}
          {brandCost && (
            <div
              className="bg-white border p-4 mb-3 flex flex-wrap items-baseline gap-x-6 gap-y-1"
              style={{ borderColor: "var(--bp-border)" }}
              data-testid="brand-cost-total"
            >
              <div className="uppercase text-[10px] tracking-widest" style={{ color: "var(--bp-text-muted)" }}>
                Total cost (all runs)
              </div>
              <div className="font-mono text-sm" data-testid="brand-cost-runs">
                {brandCost.run_count} run{brandCost.run_count === 1 ? "" : "s"}
              </div>
              <div className="font-mono text-sm" data-testid="brand-cost-calls">
                {brandCost.llm_calls} LLM call{brandCost.llm_calls === 1 ? "" : "s"}
              </div>
              <div className="font-mono text-sm" data-testid="brand-cost-tokens">
                {brandCost.input_tokens.toLocaleString()} in tokens · {brandCost.output_tokens.toLocaleString()} out tokens
              </div>
            </div>
          )}

          {recentRuns.length === 0 ? (
            <div
              className="bg-white border p-4 text-xs"
              style={{ borderColor: "var(--bp-border)", color: "var(--bp-text-muted)" }}
              data-testid="recent-crawls-empty"
            >
              No pipeline runs yet for this brand. Click "Force crawl now" to enqueue the first one.
            </div>
          ) : (
            <div className="bg-white border" style={{ borderColor: "var(--bp-border)" }}>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b text-left" style={{ borderColor: "var(--bp-border)" }}>
                    <th className="px-3 py-2 uppercase tracking-widest">Kind</th>
                    <th className="px-3 py-2 uppercase tracking-widest">State</th>
                    <th className="px-3 py-2 uppercase tracking-widest">Outcome</th>
                    <th className="px-3 py-2 uppercase tracking-widest">Started</th>
                    <th className="px-3 py-2 uppercase tracking-widest">Duration</th>
                    <th className="px-3 py-2 uppercase tracking-widest" title="LLM calls · input tokens / output tokens">LLM cost</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {recentRuns.map((r) => {
                    const started = r.started_at ? new Date(r.started_at) : null;
                    const finished = r.finished_at ? new Date(r.finished_at) : null;
                    let duration = "—";
                    if (started && finished) {
                      const ms = finished.getTime() - started.getTime();
                      if (ms >= 0) {
                        const s = Math.floor(ms / 1000);
                        duration = s < 90 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
                      }
                    } else if (started && !finished) {
                      duration = "in flight";
                    }
                    const stateColor = ({
                      SUCCEEDED: "#166534", FAILED: "#B91C1C",
                      NEEDS_ATTENTION: "#B97700", AWAITING_APPROVAL: "#1D4ED8",
                      HELD: "#B97700", RUNNING: "#1D4ED8", QUEUED: "#6B7280",
                      PENDING: "#6B7280",
                    })[r.state] || "var(--bp-text-muted)";
                    const cc = r.cost_counters || {};
                    const inTok = cc.total_input_tokens || 0;
                    const outTok = cc.total_output_tokens || 0;
                    const calls = cc.llm_calls || 0;
                    const fmtK = (n) => (n < 1000 ? String(n) : `${(n / 1000).toFixed(1)}k`);
                    return (
                      <tr
                        key={r.id}
                        className="border-b last:border-b-0 hover:bg-gray-50 cursor-pointer"
                        style={{ borderColor: "var(--bp-border)" }}
                        onClick={() => nav(`/admin/pipeline/runs/${r.id}`)}
                        title="Open run detail"
                        data-testid={`recent-crawls-row-${r.id}`}
                      >
                        <td className="px-3 py-2 font-mono">{r.kind || "—"}</td>
                        <td className="px-3 py-2 font-mono" style={{ color: stateColor }}>{r.state}</td>
                        <td className="px-3 py-2 font-mono" style={{ color: "var(--bp-text-muted)" }}>{r.outcome || "—"}</td>
                        <td className="px-3 py-2 font-mono" style={{ color: "var(--bp-text-muted)" }}>
                          {started ? started.toLocaleString() : "—"}
                        </td>
                        <td className="px-3 py-2 font-mono" style={{ color: "var(--bp-text-muted)" }}>{duration}</td>
                        <td className="px-3 py-2 font-mono" style={{ color: "var(--bp-text-muted)" }} data-testid={`recent-crawls-cost-${r.id}`}>
                          {calls === 0 && inTok === 0 && outTok === 0
                            ? "—"
                            : `${calls} calls · ${fmtK(inTok)}/${fmtK(outTok)}`}
                        </td>
                        <td className="px-3 py-2 font-mono text-right" style={{ color: "var(--bp-text-muted)" }}>
                          {r.held_reason ? <span title={r.held_reason} className="mr-2">· {r.held_reason.slice(0, 24)}</span> : null}
                          <span className="bp-link">Open →</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Actions */}
        <section className="mt-10 flex flex-wrap gap-3 sticky bottom-0 py-4 border-t bg-white/95 backdrop-blur" style={{ borderColor: "var(--bp-border)" }}>
          <Button onClick={save} disabled={busy} className="rounded-none" data-testid="btn-save"><Save size={14} className="mr-1" /> Save draft</Button>
          <Button onClick={submitReview} disabled={busy || reviewState !== "DRAFT"} variant="outline" className="rounded-none" data-testid="btn-submit-review"><Send size={14} className="mr-1" /> Submit for review</Button>
          <Button onClick={approve} disabled={busy || !canApprove} variant="outline" className="rounded-none" data-testid="btn-approve"><Check size={14} className="mr-1" /> {approveLabel}</Button>
          {isAdmin && (
            <Button
              onClick={deleteBrand}
              disabled={busy}
              variant="outline"
              className="rounded-none"
              style={{ color: "#B91C1C", borderColor: "#B91C1C" }}
              data-testid="btn-delete-brand"
              title="Permanently remove this brand from the platform"
            >
              Delete
            </Button>
          )}
          <div className="ml-auto text-xs font-mono self-center" style={{ color: "var(--bp-text-muted)" }}>
            {reviewState === "PENDING_REVIEW" && !canApprove && "You submitted this profile — a different editor must approve (4-eyes rule)."}
            {reviewState === "PENDING_REVIEW" && canApprove && "Awaiting your approval."}
            {isAdmin && (reviewState === "NEEDS_ATTENTION" || reviewState === "DRAFT") && canApprove && (
              <span data-testid="admin-force-publish-hint">Admin override — this profile has partial data. Publish anyway?</span>
            )}
            {isAdmin && isSelfSubmitted && reviewState !== "PUBLISHED" && !isGeneralAdmin && (
              <span>4-eyes rule: another editor must approve your own submission.</span>
            )}
            {isGeneralAdmin && isSelfSubmitted && reviewState !== "PUBLISHED" && (
              <span data-testid="general-admin-self-approve-hint">General admin — you may approve your own submission.</span>
            )}
          </div>
        </section>
      </div>

      {/* Force-crawl progress toast — bottom-right, polls the run
          state every 4s until terminal, then shows outcome + reload
          hint. Dismissible. */}
      {progress && (() => {
        const TERMINAL = new Set([
          "SUCCEEDED", "FAILED", "NEEDS_ATTENTION",
          "AWAITING_APPROVAL", "HELD", "PUBLISHED_ACCESS_RESTRICTED",
        ]);
        const isDone = TERMINAL.has(progress.state);
        const stateColor = ({
          SUCCEEDED: "#166534", FAILED: "#B91C1C",
          NEEDS_ATTENTION: "#B97700", AWAITING_APPROVAL: "#1D4ED8",
          HELD: "#B97700", RUNNING: "#1D4ED8",
          QUEUED: "#6B7280", PENDING: "#6B7280",
        })[progress.state] || "var(--bp-text-muted)";
        return (
          <div
            className="fixed bottom-6 right-6 z-50 shadow-lg border p-4"
            style={{
              background: "white", borderColor: "var(--bp-border)",
              minWidth: 280, maxWidth: 380,
            }}
            data-testid="force-crawl-progress-toast"
          >
            <div className="flex items-baseline justify-between mb-2">
              <div className="uppercase text-[10px] tracking-widest" style={{ color: "var(--bp-text-muted)" }}>
                Force crawl · {progress.kind || "REPROCESS"}
              </div>
              <button
                onClick={() => setProgress(null)}
                className="text-xs bp-link"
                data-testid="force-crawl-progress-dismiss"
                title="Dismiss"
              >
                ✕
              </button>
            </div>
            <div className="text-sm font-mono mb-1" data-testid="force-crawl-progress-state">
              <span style={{ color: stateColor }}>{progress.state}</span>
              <span className="ml-2 text-[10px]" style={{ color: "var(--bp-text-muted)" }}>
                run {progress.runId.slice(0, 8)}…
              </span>
            </div>
            <div className="h-1.5 w-full mt-2" style={{ background: "#EEE" }}>
              <div
                className={`h-full ${isDone ? "" : "animate-pulse"}`}
                style={{
                  width: isDone ? "100%" : "40%",
                  background: isDone ? stateColor : "#0369A1",
                  transition: "width 400ms",
                }}
              />
            </div>
            {isDone && (
              <div className="mt-2 text-xs" style={{ color: stateColor }}>
                {progress.state === "SUCCEEDED" && "Run completed successfully. Profile updated."}
                {progress.state === "AWAITING_APPROVAL" && "Run finished — profile is waiting for review."}
                {progress.state === "NEEDS_ATTENTION" && "Run needs attention — check the Pipeline monitor."}
                {progress.state === "FAILED" && "Run failed — check the Pipeline monitor for details."}
                {progress.state === "HELD" && "Run held — check the reason in the recent crawls table."}
              </div>
            )}
          </div>
        );
      })()}
    </AdminLayout>
  );
}
